import { Box, Typography, IconButton, CircularProgress, Tooltip, Chip } from "@mui/material";
import { Visibility, Download } from "@mui/icons-material";
import EditIcon from "@/assets/svgs/edit.svg";
import DeleteIcon from "@/assets/svgs/garbage.svg";

import { IconImage } from "@/components";
import { formatDateOrTime } from "@/utils";
import { excel_img } from "@/constants";
import { tokens } from "@/tokens";

interface CertificateActionsProps {
  action: (type: "view" | "download" | "delete" | "edit", cert: CertificateSummary) => void;
  actionsLoading: {
    view: string[];
    download: string[];
    delete: string[];
    edit: string[];
  };
}

export const columns = ({
  action,
  actionsLoading,
}: CertificateActionsProps): IColumn<CertificateSummary>[] => [
  {
    name: "File Name",
    key: "fileName",
    align: "left",
    width: "250px",
    sorting: true,
    component: (cert) => (
      <Box display="flex" alignItems="center" gap={1}>
        <IconImage src={excel_img} size="24px" />
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: tokens.text.primary,
              fontWeight: tokens.fontWeight.semibold,
              cursor: "pointer",
              "&:hover": {
                color: tokens.brand.primary,
                textDecoration: "underline",
              },
            }}
            onClick={() => action("view", cert)}
          >
            {cert.fileName}
          </Typography>
          <Typography variant="caption" color={tokens.text.muted}>
            {cert.itemCount} {cert.itemCount === 1 ? "item" : "items"}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    name: "Upload Date",
    key: "uploadDate",
    align: "left",
    width: "140px",
    sorting: true,
    component: (cert) => (
      <Box>
        <Typography variant="body2" color={tokens.text.primary}>
          {formatDateOrTime(cert.uploadDate, "date")}
        </Typography>
        <Typography variant="body1" fontSize={tokens.fontSize.base} color={tokens.text.muted}>
          {formatDateOrTime(cert.uploadDate, "time")}
        </Typography>
      </Box>
    ),
  },
  {
    name: "Form Type",
    key: "formType",
    align: "left",
    width: "120px",
    sorting: true,
    component: (cert) => (
      <Chip
        label={cert.formType || "Unknown"}
        size="small"
        color={cert.formType === "USMCA" ? "primary" : "default"}
        variant="outlined"
      />
    ),
  },
  {
    name: "Actions",
    key: "actions",
    align: "center",
    width: "240px",
    component: (cert) => (
      <Box display="flex" gap={1} justifyContent="center">
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => action("view", cert)}
            disabled={actionsLoading.view.includes(cert._id)}
            sx={{
              color: tokens.brand.primary,
              "&:hover": {
                backgroundColor: tokens.brand.accentLight,
              },
            }}
          >
            {actionsLoading.view.includes(cert._id) ? (
              <CircularProgress size={16} />
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Edit Certificate">
          <IconButton
            size="small"
            onClick={() => action("edit", cert)}
            disabled={actionsLoading.edit.includes(cert._id)}
            sx={{
              color: tokens.status.info,
              "&:hover": {
                backgroundColor: tokens.status.infoBg,
              },
            }}
          >
            {actionsLoading.edit.includes(cert._id) ? (
              <CircularProgress size={16} />
            ) : (
              <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Download Certificate">
          <IconButton
            size="small"
            onClick={() => action("download", cert)}
            disabled={actionsLoading.download.includes(cert._id)}
            sx={{
              color: tokens.status.success,
              "&:hover": {
                backgroundColor: tokens.status.successBg,
              },
            }}
          >
            {actionsLoading.download.includes(cert._id) ? (
              <CircularProgress size={16} />
            ) : (
              <Download fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete Certificate">
          <IconButton
            size="small"
            onClick={() => action("delete", cert)}
            disabled={actionsLoading.delete.includes(cert._id)}
            sx={{
              color: tokens.status.danger,
              "&:hover": {
                backgroundColor: tokens.status.dangerBg,
              },
            }}
          >
            {actionsLoading.delete.includes(cert._id) ? (
              <CircularProgress size={16} />
            ) : (
              <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    ),
  },
];